import moment from 'moment'
import { MODULE_FALLBACKS } from '../utils/constants'
import { deriveTimelineFromState } from './deriveTimelineProjection'
import {
  getFirstScheduleContentDay,
  getGridDayCount,
  GRID_DAYS_MIN,
  studyDurationFromListToDays,
} from '../utils/studyTimelineGridUtils'

export const SET_VIEW_TYPE = 'studyTimeline/SET_VIEW_TYPE'
export const SET_SELECTED_ARM_ID = 'studyTimeline/SET_SELECTED_ARM_ID'
export const SET_INCLUDE_VISIT_CYCLE = 'studyTimeline/SET_INCLUDE_VISIT_CYCLE'
export const SET_USE_ABSOLUTE_START_DATE = 'studyTimeline/SET_USE_ABSOLUTE_START_DATE'
export const SET_ABSOLUTE_START_DATE = 'studyTimeline/SET_ABSOLUTE_START_DATE'
export const SET_FILTER_BY_INSTRUMENT = 'studyTimeline/SET_FILTER_BY_INSTRUMENT'
export const SET_SELECTED_INSTRUMENT_ID = 'studyTimeline/SET_SELECTED_INSTRUMENT_ID'
export const SET_SELECTED_DAY = 'studyTimeline/SET_SELECTED_DAY'
export const SET_VISIT_SCHEDULES = 'studyTimeline/SET_VISIT_SCHEDULES'
export const SET_INSTRUMENTS = 'studyTimeline/SET_INSTRUMENTS'
export const SET_EXPECTED_STUDY_ID = 'studyTimeline/SET_EXPECTED_STUDY_ID'
export const SET_STUDY_DURATION_FROM_LIST = 'studyTimeline/SET_STUDY_DURATION_FROM_LIST'
export const RESET_STUDY_TIMELINE = 'studyTimeline/RESET_STUDY_TIMELINE'
export const SET_GRID_LOADED = 'studyTimeline/SET_GRID_LOADED'
export const SET_PROJECTION_MODE = 'studyTimeline/SET_PROJECTION_MODE'
export const SET_PROJECTION_PARTICIPANT_ID = 'studyTimeline/SET_PROJECTION_PARTICIPANT_ID'
export const SET_PARTICIPANT_VISITS = 'studyTimeline/SET_PARTICIPANT_VISITS'

export const initialState = {
  viewType: 'overview',
  studyVisitSchedule: [],
  arms: [],
  selectedArmId: null,
  includeVisitCycle: false,
  useAbsoluteStartDate: true,
  absoluteStartDate: moment().format('YYYY-MM-DD'),
  filterByInstrument: false,
  instruments: [],
  selectedInstrumentId: null,
  days: Array.from({ length: GRID_DAYS_MIN }, (_, index) => index + 1),
  scheduledItems: [],
  cycleRanges: [],
  selectedDay: 1,
  gridLoaded: false,
  expectedStudyId: null,
  studyDurationFromList: null,
  projectionMode: 'template',
  projectionParticipantId: null,
  participantVisits: null,
}

export default function studyTimelineReducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_VIEW_TYPE:
      return {
        ...state,
        viewType: action.payload,
      }
    case SET_SELECTED_ARM_ID: {
      const armDerived = deriveTimelineFromState({ ...state, selectedArmId: action.payload })
      const currentDayCount = state.days && state.days.length > 0 ? state.days.length : null
      const armDays =
        currentDayCount != null
          ? currentDayCount
          : getGridDayCount(
            armDerived.items || [],
            armDerived.cycleRanges || [],
            studyDurationFromListToDays(state.studyDurationFromList),
          )
      return {
        ...state,
        selectedArmId: action.payload,
        scheduledItems: armDerived.items || [],
        cycleRanges: armDerived.cycleRanges || [],
        days: Array.from({ length: armDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(armDerived.items || [], armDerived.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    }
    case SET_INCLUDE_VISIT_CYCLE: {
      const newIncludeVisitCycle = action.payload
      const firstArmId = state.arms?.length > 0 && state.arms[0]?.id != null ? String(state.arms[0].id) : null
      const effectiveArmId =
        newIncludeVisitCycle && firstArmId != null && (state.selectedArmId == null || state.selectedArmId === '')
          ? firstArmId
          : state.selectedArmId
      const derivedIncludeCycle = deriveTimelineFromState({
        ...state,
        selectedArmId: effectiveArmId,
        includeVisitCycle: newIncludeVisitCycle,
      })
      const currentDayCount = state.days && state.days.length > 0 ? state.days.length : GRID_DAYS_MIN
      return {
        ...state,
        includeVisitCycle: newIncludeVisitCycle,
        ...(effectiveArmId !== state.selectedArmId ? { selectedArmId: effectiveArmId } : {}),
        scheduledItems: derivedIncludeCycle.items || [],
        cycleRanges: derivedIncludeCycle.cycleRanges || [],
        days: Array.from({ length: currentDayCount }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedIncludeCycle.items || [], derivedIncludeCycle.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    }
    case SET_USE_ABSOLUTE_START_DATE: {
      const newUseAbsolute = action.payload
      const derivedOnToggle = deriveTimelineFromState({
        ...state,
        useAbsoluteStartDate: newUseAbsolute,
        absoluteStartDate: newUseAbsolute ? state.absoluteStartDate : null,
      })
      const toggleDays = getGridDayCount(
        derivedOnToggle.items || [],
        derivedOnToggle.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        useAbsoluteStartDate: newUseAbsolute,
        absoluteStartDate: newUseAbsolute ? state.absoluteStartDate : null,
        scheduledItems: derivedOnToggle.items || [],
        cycleRanges: derivedOnToggle.cycleRanges || [],
        days: Array.from({ length: toggleDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedOnToggle.items || [], derivedOnToggle.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    }
    case SET_ABSOLUTE_START_DATE: {
      const derivedOnDateChange = deriveTimelineFromState({
        ...state,
        absoluteStartDate: action.payload,
      })
      const dateChangeDays = getGridDayCount(
        derivedOnDateChange.items || [],
        derivedOnDateChange.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        absoluteStartDate: action.payload,
        scheduledItems: derivedOnDateChange.items || [],
        cycleRanges: derivedOnDateChange.cycleRanges || [],
        days: Array.from({ length: dateChangeDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derivedOnDateChange.items || [], derivedOnDateChange.cycleRanges || []) ?? state.selectedDay ?? 1,
        gridLoaded: false,
      }
    }
    case SET_FILTER_BY_INSTRUMENT:
      return {
        ...state,
        filterByInstrument: action.payload,
        selectedInstrumentId: action.payload ? state.selectedInstrumentId : null,
      }
    case SET_SELECTED_INSTRUMENT_ID:
      return {
        ...state,
        selectedInstrumentId: action.payload,
      }
    case SET_SELECTED_DAY:
      return {
        ...state,
        selectedDay: action.payload,
      }
    case SET_EXPECTED_STUDY_ID:
      return {
        ...state,
        expectedStudyId: action.payload != null ? action.payload : null,
      }
    case SET_STUDY_DURATION_FROM_LIST: {
      const minDays = studyDurationFromListToDays(action.payload)
      const newDayCount = getGridDayCount(
        state.scheduledItems || [],
        state.cycleRanges || [],
        minDays,
      )
      return {
        ...state,
        studyDurationFromList: action.payload,
        days: Array.from({ length: newDayCount }, (_, i) => i + 1),
      }
    }
    case SET_VISIT_SCHEDULES: {
      const payload = action.payload
      const isNewShape = payload && typeof payload === 'object' && !Array.isArray(payload) && 'studyId' in payload
      const studyId = isNewShape ? payload.studyId : null
      const data = isNewShape ? payload.data : payload
      const expectedId = state.expectedStudyId != null ? state.expectedStudyId : null
      if (studyId != null && expectedId != null && String(studyId) !== String(expectedId)) {
        return state
      }
      const rawDuration = payload?.study_duration
      const nextStudyDurationFromList =
        rawDuration != null
          ? (typeof rawDuration === 'object' && rawDuration.study_duration != null
              ? rawDuration
              : { study_duration: rawDuration })
          : state.studyDurationFromList
      const studyVisitSchedule = Array.isArray(data) ? data : (Array.isArray(payload) ? payload : [])
      const arms = studyVisitSchedule.map(arm => ({
        id: arm.id,
        name: arm.name,
        ...arm,
      }))
      const firstArmId = arms.length > 0 ? (arms[0].id ? String(arms[0].id) : null) : null
      const newSelectedArmId = firstArmId || state.selectedArmId
      const derived = deriveTimelineFromState({
        ...state,
        arms,
        selectedArmId: newSelectedArmId,
        studyDurationFromList: nextStudyDurationFromList,
      })
      const minDaysFromList = studyDurationFromListToDays(nextStudyDurationFromList)
      const visitScheduleDays = getGridDayCount(
        derived.items || [],
        derived.cycleRanges || [],
        minDaysFromList,
      )
      return {
        ...state,
        studyVisitSchedule,
        arms,
        selectedArmId: newSelectedArmId,
        scheduledItems: derived.items || [],
        cycleRanges: derived.cycleRanges || [],
        studyDurationFromList: nextStudyDurationFromList,
        days: Array.from({ length: visitScheduleDays }, (_, i) => i + 1),
        selectedDay: getFirstScheduleContentDay(derived.items || [], derived.cycleRanges || []) ?? state.selectedDay ?? 1,
      }
    }
    case SET_INSTRUMENTS: {
      const payload = action.payload
      const isNewShape = payload && typeof payload === 'object' && !Array.isArray(payload) && 'studyId' in payload
      const instrumentsStudyId = isNewShape ? payload.studyId : null
      const instrumentsPayload = isNewShape ? payload.data : payload
      const expectedIdInstruments = state.expectedStudyId != null ? state.expectedStudyId : null
      if (instrumentsStudyId != null && expectedIdInstruments != null && String(instrumentsStudyId) !== String(expectedIdInstruments)) {
        return state
      }
      const instrumentsList = Array.isArray(instrumentsPayload) ? instrumentsPayload : []
      const instruments = instrumentsList.map(instrument => ({
        id: instrument.id,
        name:
          instrument.metadata?.display_name ||
          instrument.title ||
          instrument.name ||
          `${MODULE_FALLBACKS.INSTRUMENT_NAME_PREFIX}${instrument.id}`,
        ...instrument,
      }))
      if (state.arms && state.arms.length > 0) {
        const selectedArmId =
          state.selectedArmId ||
          (state.arms[0]?.id != null ? String(state.arms[0].id) : null)
        const derivedWithInstruments = deriveTimelineFromState({
          ...state,
          instruments,
          selectedArmId,
        })
        const instrumentsDays = getGridDayCount(
          derivedWithInstruments.items || [],
          derivedWithInstruments.cycleRanges || [],
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        return {
          ...state,
          instruments,
          selectedArmId: selectedArmId || state.selectedArmId,
          scheduledItems: derivedWithInstruments.items || [],
          cycleRanges: derivedWithInstruments.cycleRanges || [],
          days: Array.from({ length: instrumentsDays }, (_, i) => i + 1),
          selectedDay: getFirstScheduleContentDay(derivedWithInstruments.items || [], derivedWithInstruments.cycleRanges || []) ?? state.selectedDay ?? 1,
        }
      }
      if (state.arms && state.arms.length === 0) {
        const derivedNoArms = deriveTimelineFromState({
          ...state,
          arms: [],
          selectedArmId: null,
          instruments,
        })
        const noArmsDays = getGridDayCount(
          derivedNoArms.items || [],
          derivedNoArms.cycleRanges || [],
          studyDurationFromListToDays(state.studyDurationFromList),
        )
        return {
          ...state,
          instruments,
          scheduledItems: derivedNoArms.items || [],
          cycleRanges: derivedNoArms.cycleRanges || [],
          days: Array.from({ length: noArmsDays }, (_, i) => i + 1),
          selectedDay: getFirstScheduleContentDay(derivedNoArms.items || [], derivedNoArms.cycleRanges || []) ?? state.selectedDay ?? 1,
        }
      }
      return {
        ...state,
        instruments,
      }
    }
    case SET_PROJECTION_MODE: {
      const projectionMode = action.payload === 'participant' ? 'participant' : 'template'
      const derived = deriveTimelineFromState({ ...state, projectionMode })
      const dayCount = getGridDayCount(
        derived.items || [],
        derived.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        projectionMode,
        scheduledItems: derived.items || [],
        cycleRanges: derived.cycleRanges || [],
        days: Array.from({ length: dayCount }, (_, i) => i + 1),
        gridLoaded: false,
      }
    }
    case SET_PROJECTION_PARTICIPANT_ID:
      return {
        ...state,
        projectionParticipantId: action.payload,
      }
    case SET_PARTICIPANT_VISITS: {
      const derived = deriveTimelineFromState({
        ...state,
        participantVisits: action.payload,
        projectionMode: 'participant',
      })
      const dayCount = getGridDayCount(
        derived.items || [],
        derived.cycleRanges || [],
        studyDurationFromListToDays(state.studyDurationFromList),
      )
      return {
        ...state,
        participantVisits: action.payload,
        scheduledItems: derived.items || [],
        cycleRanges: derived.cycleRanges || [],
        days: Array.from({ length: dayCount }, (_, i) => i + 1),
      }
    }
    case SET_GRID_LOADED:
      return {
        ...state,
        gridLoaded: action.payload,
      }
    case RESET_STUDY_TIMELINE:
      return {
        ...initialState,
      }
    default:
      return state
  }
}
